import React, { useMemo, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { Box, Button, Typography } from '@mui/material';
import StoryShelf from '../components/StoryShelf/StoryShelf.js';
import { Storybook3DReader } from '../components/Storybook/index.js';

const PREVIEW_STORIES = [
  {
    id: 'preview-moon-garden',
    title: 'The Moon Garden',
    cover_image_url: '/preview/story-art/moon-garden.png',
    story_content: 'When Mina found a silver seed beneath her pillow, she planted it beside the window. By morning, a moonlit garden had grown — and every flower remembered a wish.',
    pages: [
      { text: 'When Mina found a silver seed beneath her pillow, she planted it beside the window.', sceneDescription: 'A child planting a silver seed beneath a moonlit bedroom window.', imageUrl: '/preview/story-art/moon-garden-page-1.png' },
      { text: 'By morning, a moonlit garden had grown — and every flower remembered a wish.', sceneDescription: 'A glowing garden of moon flowers under a starry sky.', imageUrl: '/preview/story-art/moon-garden-page-2.png' },
      { text: 'Mina chose one wish to share with everyone in her neighborhood.', sceneDescription: 'A child sharing glowing flowers with neighbors at dawn.', imageUrl: '/preview/story-art/moon-garden-page-3.png' },
    ],
    created_at: '2026-08-04T10:00:00.000Z',
    images_status: 'completed',
  },
  {
    id: 'preview-brave-little-cloud',
    title: 'The Brave Little Cloud',
    cover_image_url: '/preview/story-art/brave-little-cloud.png',
    story_content: 'A small cloud wanted to help the thirsty garden below. It gathered its courage, opened its heart, and made the gentlest rain.',
    pages: [
      { text: 'A small cloud wanted to help the thirsty garden below.', sceneDescription: 'A tiny friendly cloud looking down at a dry garden.', imageUrl: '/preview/story-art/brave-little-cloud.png' },
      { text: 'It gathered its courage, opened its heart, and made the gentlest rain.', sceneDescription: 'A small cloud raining softly over bright flowers.', imageUrl: '/preview/story-art/brave-little-cloud.png' },
    ],
    created_at: '2026-08-02T10:00:00.000Z',
    images_status: 'completed',
  },
  {
    id: 'preview-pockets-of-calm',
    title: 'Pockets of Calm',
    cover_image_url: '/preview/story-art/pockets-of-calm.png',
    story_content: 'When the day felt too loud, Theo discovered that calm could fit anywhere: inside a breath, beneath a blanket, or in the space between two kind words.',
    pages: [
      { text: 'When the day felt too loud, Theo discovered that calm could fit anywhere.', sceneDescription: 'A child finding a quiet corner filled with warm light.', imageUrl: '/preview/story-art/pockets-of-calm.png' },
      { text: 'Inside a breath, beneath a blanket, or in the space between two kind words.', sceneDescription: 'A peaceful bedtime scene with a blanket and soft stars.', imageUrl: '/preview/story-art/pockets-of-calm.png' },
    ],
    created_at: '2026-07-29T10:00:00.000Z',
    images_status: 'generating',
  },
  {
    id: 'preview-forest-friends',
    title: 'Forest Friends',
    cover_image_url: '/preview/story-art/forest-friends.png',
    story_content: 'At the edge of the forest, three unlikely friends built a little library where anyone could borrow a story and leave a kindness behind.',
    pages: [
      { text: 'At the edge of the forest, three unlikely friends built a little library.', sceneDescription: 'A fox, rabbit, and child building a tiny forest library.', imageUrl: '/preview/story-art/forest-friends.png' },
      { text: 'Anyone could borrow a story and leave a kindness behind.', sceneDescription: 'Forest animals reading together beside a tree library.', imageUrl: '/preview/story-art/forest-friends.png' },
    ],
    created_at: '2026-07-21T10:00:00.000Z',
    images_status: 'completed',
  },
];

function PreviewReader({ stories }) {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const story = stories.find((item) => item.id === storyId) || stories[0];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7f3eb', px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        <Button startIcon={<ArrowLeft size={16} />} onClick={() => navigate('/')} sx={{ mb: 2 }}>
          Back to shelf
        </Button>
        <Storybook3DReader
          title={story.title}
          author={story.author_name || 'Make It Mindful'}
          pages={story.pages}
          coverImageUrl={story.cover_image_url}
          isGeneratingImages={story.images_status === 'generating'}
          narration={{ mode: 'browser' }}
          showControls
        />
      </Box>
    </Box>
  );
}

function PreviewShelf({ stories, setStories }) {
  const [selectedStoryId, setSelectedStoryId] = useState(stories[0]?.id);
  const selectedStory = useMemo(
    () => stories.find((story) => story.id === selectedStoryId) || stories[0],
    [selectedStoryId, stories]
  );

  const handleDelete = (story) => {
    setStories((current) => current.filter((item) => item.id !== story.id));
    setSelectedStoryId(stories.find((item) => item.id !== story.id)?.id);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f7f3eb', px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 4 } }}>
      <Box sx={{ maxWidth: 1240, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Link to="/" style={{ color: '#302a25', textDecoration: 'none', fontWeight: 800 }}>Make It Mindful</Link>
        <Typography variant="caption" sx={{ color: '#756d65', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Sparkles size={14} /> Local visual preview
        </Typography>
      </Box>
      {stories.length > 0 ? (
        <StoryShelf
          stories={stories}
          selectedStory={selectedStory}
          onSelect={(story) => setSelectedStoryId(story.id)}
          onShare={() => {}}
          onDelete={handleDelete}
        />
      ) : (
        <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: 12 }}>
          <Typography variant="h4" gutterBottom>Preview shelf is empty</Typography>
          <Button component={Link} to="/" variant="contained">Reload preview</Button>
        </Box>
      )}
    </Box>
  );
}

export default function LocalPreview() {
  const [stories, setStories] = useState(PREVIEW_STORIES);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/story/:storyId" element={<PreviewReader stories={stories} />} />
        <Route path="/" element={<PreviewShelf stories={stories} setStories={setStories} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
